import { ChatLang, ChatMode } from '../types';

const API_BASE_URL = 'https://vngpt.onrender.com';

export interface ChatPayload {
  message: string;
  mode: ChatMode;
  lang: ChatLang;
  sessionId?: string | null;
  attachments?: File[];
}

export interface APISource {
  title: string;
  url: string;
  snippet: string;
  rank: number;
  reliability?: number | null;
  notes?: string | null;
}

export interface APIChatResponse {
  success: boolean;
  data?: {
    answer: string;
    sources: APISource[];
    session_id?: string | null;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * Send chat request to VNGPT backend.
 * - With attachments → POST /api/chat/mm (FormData)
 * - Without attachments → POST /api/chat (JSON)
 *
 * Both endpoints receive: message, mode, lang, session_id
 */
export const sendMessage = async (payload: ChatPayload): Promise<APIChatResponse> => {
  const hasAttachments = payload.attachments && payload.attachments.length > 0;

  try {
    let response: Response;

    if (hasAttachments) {
      // ─── Multimodal: FormData ───
      const formData = new FormData();
      formData.append('message', payload.message);
      formData.append('mode', payload.mode);
      formData.append('lang', payload.lang);

      // Send session_id if we have one (continuing a conversation)
      if (payload.sessionId) {
        formData.append('session_id', payload.sessionId);
      }

      payload.attachments!.forEach((file) => {
        formData.append('attachments', file);
      });

      response = await fetch(`${API_BASE_URL}/api/chat/mm`, {
        method: 'POST',
        body: formData,
      });
    } else {
      // ─── Text-only: JSON ───
      const body: Record<string, any> = {
        message: payload.message,
        mode: payload.mode,
        lang: payload.lang,
      };

      // Only include session_id if it exists
      if (payload.sessionId) {
        body.session_id = payload.sessionId;
      }

      response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    }

    if (!response.ok) {
      if (response.status === 429) {
        return {
          success: false,
          error: {
            code: 'RATE_LIMITED',
            message: 'Too many requests. Please wait a moment and try again.',
          },
        };
      }
      return {
        success: false,
        error: {
          code: 'HTTP_ERROR',
          message: `Server returned ${response.status}. Please try again.`,
        },
      };
    }

    const data: APIChatResponse = await response.json();
    return data;
  } catch (error: any) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message:
          error?.message === 'Failed to fetch'
            ? 'Unable to connect to the server. Please check your connection.'
            : `Request failed: ${error?.message || 'Unknown error'}`,
      },
    };
  }
};

/**
 * Simulate streaming by revealing the full response word by word.
 */
export const simulateStreaming = (
  fullText: string,
  onChunk: (chunk: string) => void,
  onComplete: () => void,
  speed: number = 12
): (() => void) => {
  const tokens: string[] = [];
  let current = '';

  for (let i = 0; i < fullText.length; i++) {
    const char = fullText[i];
    current += char;

    if (
      char === ' ' ||
      char === '\n' ||
      char === '.' ||
      char === ',' ||
      char === '!' ||
      char === '?' ||
      char === ':' ||
      char === ';' ||
      char === ')' ||
      char === ']'
    ) {
      tokens.push(current);
      current = '';
    }
  }
  if (current) tokens.push(current);

  let index = 0;
  let cancelled = false;

  const emit = () => {
    if (cancelled) return;

    const batchSize = Math.max(1, Math.floor(tokens.length / 200));
    let batch = '';

    for (let i = 0; i < batchSize && index < tokens.length; i++, index++) {
      batch += tokens[index];
    }

    if (batch) onChunk(batch);

    if (index < tokens.length) {
      setTimeout(emit, speed + Math.random() * 8);
    } else {
      onComplete();
    }
  };

  setTimeout(emit, 50);
  return () => { cancelled = true; };
};

/**
 * Generate a short title from the first message.
 */
export const generateChatTitle = async (firstMessage: string): Promise<string> => {
  const words = firstMessage.split(' ').slice(0, 6);
  let title = words.join(' ');
  if (firstMessage.split(' ').length > 6) title += '...';
  return title;
};